import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  ShoppingCart,
  Clock,
  Truck,
  DollarSign,
  Plus,
  Package,
} from 'lucide-react';

interface Order {
  id: string;
  order_number: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  items_count: number;
  total: number;
  payment_status: 'pending' | 'paid' | 'refunded';
  created_at: string;
}

interface OrdersResponse {
  orders: Order[];
  metrics: {
    total_orders: number;
    pending: number;
    shipped: number;
    revenue: number;
  };
}

const statusColors: Record<string, string> = {
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  processing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  shipped: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  delivered: 'bg-muted-foreground/20 text-muted-foreground border-border',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const paymentColors: Record<string, string> = {
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  paid: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  refunded: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export default function OrdersPage() {
  const { data, isLoading } = useApi<OrdersResponse>('/api/orders');
  const [showNewOrder, setShowNewOrder] = useState(false);

  const metrics = data?.metrics ?? { total_orders: 0, pending: 0, shipped: 0, revenue: 0 };
  const orders = data?.orders ?? [];

  const metricCards = [
    { label: 'Total Orders', value: metrics.total_orders, icon: ShoppingCart, color: 'text-emerald-400' },
    { label: 'Pending', value: metrics.pending, icon: Clock, color: 'text-amber-400' },
    { label: 'Shipped', value: metrics.shipped, icon: Truck, color: 'text-blue-400' },
    { label: 'Revenue', value: `$${metrics.revenue.toLocaleString()}`, icon: DollarSign, color: 'text-emerald-400' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="h-6 w-6 text-emerald-500" />
          <h1 className="text-2xl font-bold text-foreground">Store & Orders</h1>
        </div>
        <button
          onClick={() => setShowNewOrder(!showNewOrder)}
          className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-foreground hover:bg-emerald-500 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Order
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metricCards.map((m) => (
          <div key={m.label} className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{m.label}</span>
              <m.icon className={`h-5 w-5 ${m.color}`} />
            </div>
            <p className="mt-2 text-2xl font-semibold text-foreground">{m.value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Order #</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Items</th>
                <th className="px-4 py-3 font-medium">Total</th>
                <th className="px-4 py-3 font-medium">Payment</th>
                <th className="px-4 py-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Loading orders...</td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No orders found</td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-foreground">{order.order_number}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusColors[order.status] ?? ''}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground">{order.items_count}</td>
                    <td className="px-4 py-3 text-foreground">${order.total.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${paymentColors[order.payment_status] ?? ''}`}>
                        {order.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
